import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Grid,
  styled,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  IconButton,
} from '@mui/material'
import {
  faMapMarkerAlt,
  faBuilding,
  faMagnifyingGlass,
  faTimes,
  faHeart,
  faCircleExclamation,
  faCalendarAlt,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SelectInputObject } from '../../../components/inputs'
import Api from '../../../services/api.service'
import { handlingErrors } from '../../../utils'
import { toast, ToastContainer, Slide } from 'react-toastify'
import lightTheme from '../../../styles/theme/lightTheme'
import { useNavigate } from 'react-router-dom'
import AppPagination from '../../../components/paginations'
import { debounce, hasPermission, normalizeText } from '../../../config'
import DetailCandidatEvent from '../../../components/modals/DetailCandidatEvent'
import AppointmentModal from '../../../components/modals/AppointmentModal'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { acces_match } from '../../../data'
import NoAccess from '../Errors/NoAccess'
import { SectionsHeading } from '../../../components/headings'

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
  },
  '&:before': {
    content: '""',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px 16px 0 0',
  },
}))

const GridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '20px',
}))

const CustomAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderWidth: 0,
  borderRadius: 0,
  overflow: 'hidden',
  fontFamily: 'Century Gothic',
  fontSize: 12,
  lineHeight: '140%',
  color: lightTheme.palette.grey.grey,

  '& .MuiOutlinedInput-root': {
    height: '100%',
    borderRadius: 0,
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& .MuiInputBase-input': {
    padding: '0 14px',
    textAlign: 'center',
  },

  '& .MuiAutocomplete-paper': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginTop: '8px',
    maxHeight: '240px',
    overflow: 'auto',
    border: '1px solid #e0ecef',
  },

  '& .MuiAutocomplete-option': {
    padding: '12px 16px',
    fontSize: 14,
    fontFamily: 'Century Gothic',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #f0f0f0',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#667eea',
      color: '#FFFFFF',
    },
  },

  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },

  [theme.breakpoints.up('lg')]: {
    fontSize: 14,
  },
}))

const WrapperButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const FavoriteCandidats = () => {
  const { t, i18n } = useTranslation()
  const userData = useSelector((state) => state.userAuth.user)
  const hasPerm = hasPermission(userData.data, acces_match)
  const navigate = useNavigate()
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState(null)
  const [favoriteStatuses, setFavoriteStatuses] = useState({})
  const [favoriteIds, setFavoriteIds] = useState({})
  const [loading, setLoading] = useState(false)
  const [openDetailCandidat, setOpenDetailCandidat] = useState(false)
  const [id, setId] = useState(null)
  const [search, setSearch] = useState('')
  const [candidat, setCandidat] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState()
  const [page, setPage] = useState(
    sessionStorage.getItem('pageCandidat')
      ? parseInt(sessionStorage.getItem('pageCandidat'))
      : 1
  )
  const [count, setCount] = useState(0)

  // New state for enhanced search
  const [jobTitleSuggestions, setJobTitleSuggestions] = useState([])
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [jobDebounceTimer, setJobDebounceTimer] = useState(null)
  const [filter, setFilter] = useState([
    { id: 5, titre: 'ID', filtre: 'id' },
    { id: 0, titre: t('filter.place'), filtre: 'search' },
    {
      id: 1,
      titre: t('filter.poste'),
      filtre: 'candidat.poste.titreNormalized',
    },
    {
      id: 2,
      titre: t('filter.competences'),
      filtre: 'candidat.competences',
    },
    { id: 3, titre: t('filter.softskils'), filtre: 'candidat.softskils' },
    { id: 4, titre: t('filter.values'), filtre: 'candidat.valeurs' },
  ])
  const filter2 = 'candidat.poste.titreEnNormalized'
  const [filterSelect, setFilterSelect] = useState(filter[0])
  const handleSave = useCallback(() => {
    setOpen(false)
  }, [])

  // Enhanced search handlers
  const handleJobChange = (event, newValue) => {
    setSearch(newValue?.value || '')
  }

  const handleClearSearch = () => {
    setSearch('')
    setJobTitleSuggestions([])
  }

  const handleJobTitleChange = async (event, value) => {
    const searchValue = value

    // Clear existing timer
    if (jobDebounceTimer) {
      clearTimeout(jobDebounceTimer)
    }

    // Set new timer to search after user stops typing
    const newTimer = setTimeout(async () => {
      if (searchValue && searchValue.length > 2) {
        try {
          const jobs = await getPostes(searchValue)
          const jobSuggestions = jobs.map((job) => ({
            value: job.intitule || job.titre || job.title,
            label: job.intitule || job.titre || job.title,
          }))
          setJobTitleSuggestions(jobSuggestions)
        } catch (error) {
          console.error('Error searching jobs:', error)
          setJobTitleSuggestions([])
        }
      } else {
        setJobTitleSuggestions([])
      }
    }, 500)

    setJobDebounceTimer(newTimer)
  }

  const getPostes = async (searchQuery) => {
    let payload = {
      search: searchQuery,
    }

    payload = Object.entries(payload).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )
    setLoadingJobs(true)

    try {
      let result = await Api.poste.listWithFilter({
        status: 1,
        ...payload,
      })

      const handling = handlingErrors(result)

      if (handling.ok) {
        const res = handling.data
        setLoadingJobs(false)
        return res
      } else {
        setLoadingJobs(false)
        return []
      }
    } catch (error) {
      setLoadingJobs(false)
      return []
    }
  }
  const handleSearchChange = debounce((event) => {
    const { value } = event.target
    setSearch(value)
  }, 800)
  const handleDetailCandidat = (candidat) => {
    setOpenDetailCandidat(true)
    setCandidat(candidat)
  }
  const handleScheduleAppointment = (candidate) => {
    setSelectedCandidate(candidate)
    setShowAppointmentModal(true)
  }

  // Favorite functions
  const fetchAllUserFavorites = async () => {
    try {
      const result = await Api.favoris.listWithFilter({
        utilisateur: userData.data.id,
      })
      const res = handlingErrors(result)

      if (res.ok && res.data.length > 0) {
        // Process all favorites at once
        const newFavoriteStatuses = {}
        const newFavoriteIds = {}

        res.data.forEach((favorite) => {
          const favoriId = favorite.favori?.id || favorite.favori
          newFavoriteStatuses[favoriId] = true
          newFavoriteIds[favoriId] = favorite.id
        })

        setFavoriteStatuses(newFavoriteStatuses)
        setFavoriteIds(newFavoriteIds)
      }
    } catch (error) {
      console.error('Error fetching user favorites:', error)
    }
  }

  const addToFavorites = async (candidateId) => {
    const data = {
      utilisateur: '/api/utilisateurs/' + userData.data.id,
      favori: '/api/utilisateurs/' + candidateId,
    }

    try {
      const result = await Api.favoris.add(data)
      const res = handlingErrors(result)

      if (res.ok) {
        setFavoriteStatuses((prev) => ({ ...prev, [candidateId]: true }))
        setFavoriteIds((prev) => ({ ...prev, [candidateId]: res.data.id }))
        toast.success(t('successMessage.candidateAddedToFavorite'))
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'))
    }
  }

  const removeFromFavorites = async (candidateId) => {
    try {
      const result = await Api.favoris.delete(favoriteIds[candidateId])
      const res = handlingErrors(result)

      if (res.ok) {
        getCandidates(page)
        setFavoriteStatuses((prev) => ({ ...prev, [candidateId]: false }))
        setFavoriteIds((prev) => ({ ...prev, [candidateId]: null }))
        toast.success(t('successMessage.candidateRemovedFromFavorite'))
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'))
    }
  }

  const handleFavoriteToggle = (candidate) => {
    const candidateId = candidate.id

    if (favoriteStatuses[candidateId]) {
      removeFromFavorites(candidateId)
    } else {
      addToFavorites(candidateId)
    }
  }

  // Fetch all user favorites when component loads or candidates change
  useEffect(() => {
    fetchAllUserFavorites()
  }, [''])
  const handleCloseDetailCandidat = () => {
    setOpenDetailCandidat(false)
    //fetchData()
  }
  const handleClose = () => {
    setOpen(false)
    //fetchData()
  }
  useEffect(() => {
    // Observer les changements de langue
    const languageChangeHandler = () => {
      setFilter([
        { id: 5, titre: 'ID', filtre: 'id' },
        { id: 0, titre: t('filter.place'), filtre: 'search' },
        { id: 1, titre: t('filter.poste'), filtre: 'candidat.poste.titre' },
        {
          id: 2,
          titre: t('filter.competences'),
          filtre: 'candidat.competences',
        },
        { id: 3, titre: t('filter.softskils'), filtre: 'candidat.softskils' },
        { id: 4, titre: t('filter.values'), filtre: 'candidat.valeurs' },
      ])
    }

    i18n.on('languageChanged', languageChangeHandler)
    return () => {
      i18n.off('languageChanged', languageChangeHandler)
    }
  }, [i18n])
  useEffect(() => {
    setFilterSelect(filter.find((obj) => obj.filtre === filterSelect.filtre))
  }, [filter])

  useEffect(() => {
    if (hasPerm) {
      getCandidates(page)
    } else {
      alertEntreprise()
    }
  }, [id, search])

  const alertEntreprise = async () => {
    try {
      const result = await Api.company.alertEmail('type1')

      const res = handlingErrors(result)

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'))
      }
    } catch (error) {}
  }

  const handleClickIcon = () => {
    navigate(`/user`)
  }

  const handleFilterSelect = (event) => {
    const inputValue = event.target.value
    setFilterSelect(inputValue)
  }

  const handlePageChange = (event, page) => {
    setPage(page)
    getCandidates(page)
    sessionStorage.setItem('pageCandidat', page)
  }

  const getCandidates = async (page) => {
    let payload = {
      search: search,
    }

    payload = Object.entries(payload).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {}
    )
    setLoading(true)

    try {
      const result = await Api.favoris.listWithFilter({
        utilisateur: userData.data.id,
        ...payload,
      })

      const res = handlingErrors(result)

      if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'))
      }

      if (res.ok) {
        //setBesoinEntreprise(res.data.besoin)
        setCandidates(res.data.map((item) => item.favori))
      }

      if (res.status === 0) {
        toast.error(t('successMessage.somethingWrong'))
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'))
    }
    setLoading(false)
  }

  const handleClickToProfil = (candidatId, candidat) => {
    navigate(`/user/candidats/${candidatId}`)
  }
  const handleClickEvent = (userId) => {
    setUserId(userId)
    setOpen(true)
  }

  return (
    <main style={{ width: '100%' }}>
      <Box
        width={'100%'}
        component={'section'}
        sx={{
          padding: { xs: '40px 0 130px 0', md: '100px 0 ', lg: '80px 0' },
        }}
      >
        <Box width={'100%'} position={'relative'}>
          <Container
            maxWidth={'lg'}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '40px',
              width: '100%',
            }}
          >
            <SectionsHeading type={'animation'}>
              {t('profile.favoriteListTitle')}
            </SectionsHeading>

            <Typography variant="body" sx={{ fontSize: { xs: 12, md: 18 } }}>
              {t('profile.favoriteListDescription')}
            </Typography>

            {!hasPerm ? (
              <NoAccess />
            ) : (
              <>
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item lg={3} md={4} xs={12}>
                    <SelectInputObject
                      items={filter}
                      value={filterSelect}
                      handleChange={handleFilterSelect}
                    />
                  </Grid>
                  <Grid item lg={4} md={4} xs={12}>
                    <CustomAutocomplete
                      freeSolo
                      options={jobTitleSuggestions}
                      value={search}
                      onChange={handleJobChange}
                      onInputChange={handleJobTitleChange}
                      loading={loadingJobs}
                      noOptionsText={
                        loadingJobs
                          ? t('home.loading')
                          : jobTitleSuggestions.length === 0 &&
                            search.length > 2
                          ? t('home.noCandidatesFound')
                          : ''
                      }
                      placeholder={t('home.candidateNamePlaceholder')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={`${t('filter.searchText')}...`}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                {params.InputProps.startAdornment}
                                {loadingJobs && (
                                  <div
                                    style={{
                                      padding: '0 8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: '#666',
                                      position: 'absolute',
                                      right: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid #666',
                                        borderTop: 'none',
                                        borderLeft: 'none',
                                        animation: 'spin 1s linear infinite',
                                        borderRadius: '50%',
                                        borderRight: '2px solid transparent',
                                        borderColor: '#666 transparent',
                                        borderStyle: 'solid',
                                      }}
                                    ></div>
                                  </div>
                                )}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {search && (
                                  <IconButton
                                    disableRipple
                                    type={'button'}
                                    aria-label={'clear'}
                                    onClick={handleClearSearch}
                                    sx={{
                                      padding: '8px',
                                      marginRight: '4px',
                                      '&:hover': {
                                        backgroundColor: 'transparent',
                                      },
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faTimes}
                                      style={{
                                        color: '#9ca3af',
                                        fontSize: '16px',
                                      }}
                                    />
                                  </IconButton>
                                )}
                                {!loadingJobs && (
                                  <IconButton
                                    disableRipple
                                    type={'button'}
                                    aria-label={'search'}
                                    sx={{
                                      position: 'absolute',
                                      right: 0,
                                      padding: '8px',
                                      '&:hover': {
                                        backgroundColor: 'transparent',
                                      },
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faMagnifyingGlass}
                                      style={{
                                        color: lightTheme.palette.primary.main,
                                        fontSize: '20px',
                                      }}
                                    />
                                  </IconButton>
                                )}
                              </>
                            ),
                            style: {
                              padding: '10px 15px',
                              height: '100%',
                            },
                          }}
                          sx={{
                            width: '100%',
                            height: { xs: 38, sm: 41, lg: 45 },
                            '& .MuiOutlinedInput-root': {
                              height: '100%',
                              borderRadius: '5px',
                              border: `1px solid ${lightTheme.palette.grey.main}`,
                              backgroundColor: '#FFFFFF',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              padding: '0',
                              '& fieldset': {
                                border: 'none',
                              },
                              '&:hover fieldset': {
                                border: 'none',
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none',
                              },
                            },
                            '& .MuiInputBase-input': {
                              flex: 1,
                              color: lightTheme.palette.text.primary,
                              fontFamily: 'Century Gothic',
                              fontSize: 14,
                              lineHeight: '140%',
                              letterSpacing: '0.2px',
                              padding: '0',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {loading ? (
                  <Grid container>
                    <GridItem item lg={12} alignItems={'center'}>
                      <CircularProgress />
                    </GridItem>
                  </Grid>
                ) : (
                  <>
                    <Grid container spacing={5}>
                      {candidates.map((candidate, index) => (
                        <GridItem
                          item
                          xs={12}
                          sm={6}
                          lg={6}
                          key={candidate.id}
                          sx={{
                            position: 'relative',
                          }}
                        >
                          <StyledCard>
                            <CardContent
                              sx={{ flexGrow: 1, p: 3, position: 'relative' }}
                            >
                              {/* Candidate Header */}
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                mb={3}
                              >
                                <Box sx={{ flex: 1, mr: 2 }}>
                                  <Typography
                                    variant="h5"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '1rem',
                                      lineHeight: 1.3,
                                      color: '#1a1a1a',
                                      mb: 1,
                                    }}
                                  >
                                    {candidate.prenom} {candidate.nom}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      px: 2,
                                      py: 0.5,
                                      backgroundColor:
                                        'rgba(102, 126, 234, 0.1)',
                                      borderRadius: '20px',
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#667eea',
                                        fontSize: '0.875rem',
                                      }}
                                    >
                                      {candidate.candidat?.poste?.titre ||
                                        candidate.poste?.titre ||
                                        t('profile.lookingForJob')}
                                    </Typography>
                                  </Box>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleFavoriteToggle(candidate)
                                  }}
                                  sx={{
                                    color: favoriteStatuses[candidate.id]
                                      ? '#ef4444'
                                      : '#9ca3af',
                                    backgroundColor: favoriteStatuses[
                                      candidate.id
                                    ]
                                      ? 'rgba(239, 68, 68, 0.1)'
                                      : 'rgba(156, 163, 175, 0.1)',
                                    '&:hover': {
                                      backgroundColor: favoriteStatuses[
                                        candidate.id
                                      ]
                                        ? 'rgba(239, 68, 68, 0.2)'
                                        : 'rgba(156, 163, 175, 0.2)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <FontAwesomeIcon icon={faHeart} />
                                </IconButton>
                              </Box>

                              <Box
                                sx={{
                                  display: 'grid',
                                  gap: 1.5,
                                  mb: 3,
                                }}
                              >
                                {/* Location */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 32,
                                      height: 32,
                                      borderRadius: '8px',
                                      backgroundColor:
                                        'rgba(102, 126, 234, 0.1)',
                                      mr: 2,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faMapMarkerAlt}
                                      style={{
                                        color: '#667eea',
                                        fontSize: '14px',
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: '#374151',
                                        fontSize: '0.875rem',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      {candidate.ville ||
                                        t('profile.locationUnknown')}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Country */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 32,
                                      height: 32,
                                      borderRadius: '8px',
                                      backgroundColor:
                                        'rgba(16, 185, 129, 0.1)',
                                      mr: 2,
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faBuilding}
                                      style={{
                                        color: '#10b981',
                                        fontSize: '14px',
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: '#374151',
                                        fontSize: '0.875rem',
                                        textTransform: 'capitalize',
                                      }}
                                    >
                                      {candidate.pays ||
                                        t('profile.countryUnknown')}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Resume/Descriptif */}
                              {candidate.candidat?.resume && (
                                <Box
                                  sx={{
                                    p: 2,
                                    backgroundColor:
                                      'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                                    borderRadius: '12px',
                                    border:
                                      '1px solid rgba(102, 126, 234, 0.1)',
                                    mb: 3,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#6b7280',
                                      fontSize: '0.75rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      mb: 1,
                                      display: 'block',
                                    }}
                                  >
                                    {t('profile.candidat.descriptif')}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: '#4b5563',
                                      fontSize: '0.875rem',
                                      lineHeight: 1.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {candidate.candidat.resume}
                                  </Typography>
                                </Box>
                              )}

                              {/* Skills Tags - Competences */}
                              {candidate.candidat?.competences &&
                                candidate.candidat.competences.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#6b7280',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 1,
                                        display: 'block',
                                      }}
                                    >
                                      {t('filter.competences')}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                      }}
                                    >
                                      {candidate.candidat.competences
                                        .slice(0, 3)
                                        .map((competence, index) => (
                                          <Box
                                            key={index}
                                            sx={{
                                              px: 2,
                                              py: 0.75,
                                              backgroundColor:
                                                'rgba(99, 102, 241, 0.08)',
                                              border:
                                                '1px solid rgba(99, 102, 241, 0.2)',
                                              borderRadius: '16px',
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(99, 102, 241, 0.12)',
                                                transform: 'translateY(-1px)',
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 600,
                                                color: '#6366f1',
                                                fontSize: '0.75rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                              }}
                                            >
                                              {competence.competence ||
                                                competence.nom ||
                                                competence}
                                            </Typography>
                                          </Box>
                                        ))}
                                      {candidate.candidat.competences.length >
                                        3 && (
                                        <Box
                                          sx={{
                                            px: 2,
                                            py: 0.75,
                                            backgroundColor:
                                              'rgba(156, 163, 175, 0.08)',
                                            border:
                                              '1px solid rgba(156, 163, 175, 0.2)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#9ca3af',
                                              fontSize: '0.75rem',
                                            }}
                                          >
                                            +
                                            {candidate.candidat.competences
                                              .length - 3}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}

                              {/* Competences Annexes */}
                              {candidate.candidat?.competencesAnnexes &&
                                candidate.candidat.competencesAnnexes.length >
                                  0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#6b7280',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 1,
                                        display: 'block',
                                      }}
                                    >
                                      {t('filter.competencesAnnexes')}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                      }}
                                    >
                                      {candidate.candidat.competencesAnnexes
                                        .slice(0, 3)
                                        .map((competence, index) => (
                                          <Box
                                            key={index}
                                            sx={{
                                              px: 2,
                                              py: 0.75,
                                              backgroundColor:
                                                'rgba(16, 185, 129, 0.08)',
                                              border:
                                                '1px solid rgba(16, 185, 129, 0.2)',
                                              borderRadius: '16px',
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(16, 185, 129, 0.12)',
                                                transform: 'translateY(-1px)',
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 600,
                                                color: '#10b981',
                                                fontSize: '0.75rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                              }}
                                            >
                                              {competence.competence ||
                                                competence.nom ||
                                                competence}
                                            </Typography>
                                          </Box>
                                        ))}
                                      {candidate.candidat.competencesAnnexes
                                        .length > 3 && (
                                        <Box
                                          sx={{
                                            px: 2,
                                            py: 0.75,
                                            backgroundColor:
                                              'rgba(156, 163, 175, 0.08)',
                                            border:
                                              '1px solid rgba(156, 163, 175, 0.2)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#9ca3af',
                                              fontSize: '0.75rem',
                                            }}
                                          >
                                            +
                                            {candidate.candidat
                                              .competencesAnnexes.length - 3}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}

                              {/* Softskils */}
                              {candidate.candidat?.softskils &&
                                candidate.candidat.softskils.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#6b7280',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 1,
                                        display: 'block',
                                      }}
                                    >
                                      {t('filter.softskils')}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                      }}
                                    >
                                      {candidate.candidat.softskils
                                        .slice(0, 3)
                                        .map((softskil, index) => (
                                          <Box
                                            key={index}
                                            sx={{
                                              px: 2,
                                              py: 0.75,
                                              backgroundColor:
                                                'rgba(139, 92, 246, 0.08)',
                                              border:
                                                '1px solid rgba(139, 92, 246, 0.2)',
                                              borderRadius: '16px',
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(139, 92, 246, 0.12)',
                                                transform: 'translateY(-1px)',
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 600,
                                                color: '#8b5cf6',
                                                fontSize: '0.75rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                              }}
                                            >
                                              {softskil}
                                            </Typography>
                                          </Box>
                                        ))}
                                      {candidate.candidat.softskils.length >
                                        3 && (
                                        <Box
                                          sx={{
                                            px: 2,
                                            py: 0.75,
                                            backgroundColor:
                                              'rgba(156, 163, 175, 0.08)',
                                            border:
                                              '1px solid rgba(156, 163, 175, 0.2)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#9ca3af',
                                              fontSize: '0.75rem',
                                            }}
                                          >
                                            +
                                            {candidate.candidat.softskils
                                              .length - 3}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}

                              {/* Values */}
                              {candidate.candidat?.valeurs &&
                                candidate.candidat.valeurs.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#6b7280',
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        mb: 1,
                                        display: 'block',
                                      }}
                                    >
                                      {t('filter.values')}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                      }}
                                    >
                                      {candidate.candidat.valeurs
                                        .slice(0, 3)
                                        .map((valeur, index) => (
                                          <Box
                                            key={index}
                                            sx={{
                                              px: 2,
                                              py: 0.75,
                                              backgroundColor:
                                                'rgba(251, 191, 36, 0.08)',
                                              border:
                                                '1px solid rgba(251, 191, 36, 0.2)',
                                              borderRadius: '16px',
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                backgroundColor:
                                                  'rgba(251, 191, 36, 0.12)',
                                                transform: 'translateY(-1px)',
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 600,
                                                color: '#fbbf24',
                                                fontSize: '0.75rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                              }}
                                            >
                                              {valeur}
                                            </Typography>
                                          </Box>
                                        ))}
                                      {candidate.candidat.valeurs.length >
                                        3 && (
                                        <Box
                                          sx={{
                                            px: 2,
                                            py: 0.75,
                                            backgroundColor:
                                              'rgba(156, 163, 175, 0.08)',
                                            border:
                                              '1px solid rgba(156, 163, 175, 0.2)',
                                            borderRadius: '16px',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#9ca3af',
                                              fontSize: '0.75rem',
                                            }}
                                          >
                                            +
                                            {candidate.candidat.valeurs.length -
                                              3}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                )}
                            </CardContent>

                            <CardActions
                              sx={{
                                p: 2,
                                pt: 0,
                                display: 'flex',
                                gap: 1,
                              }}
                            >
                              <Button
                                variant="contained"
                                size="medium"
                                onClick={() =>
                                  // handleDetailCandidat(candidate)
                                  handleClickToProfil(candidate.id, candidate)
                                }
                                sx={{
                                  flexGrow: 1,
                                  py: 1.25,
                                  borderRadius: '12px',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  textTransform: 'none',
                                  background:
                                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                  boxShadow:
                                    '0 4px 14px rgba(102, 126, 234, 0.3)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    background:
                                      'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                                    boxShadow:
                                      '0 6px 20px rgba(102, 126, 234, 0.4)',
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                {t('profile.viewProfile')}
                              </Button>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleClickEvent(candidate)
                                }}
                                sx={{
                                  ml: 1,
                                  width: 44,
                                  height: 44,
                                  borderRadius: '12px',
                                  backgroundColor: 'rgba(156, 163, 175, 0.1)',
                                  border: '1px solid rgba(156, 163, 175, 0.2)',
                                  color: '#6b7280',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(156, 163, 175, 0.2)',
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                <FontAwesomeIcon icon={faCalendarAlt} />
                              </IconButton>
                            </CardActions>
                          </StyledCard>
                        </GridItem>
                      ))}
                    </Grid>

                    {/* No Results */}
                    {candidates.length === 0 && (
                      <Box textAlign="center" py={8}>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(
                            'profile.noCandidatesFound',
                            'Aucun candidat trouvé'
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            'profile.tryDifferentFilters',
                            'Essayez avec des filtres différents'
                          )}
                        </Typography>
                      </Box>
                    )}

                    {/* Pagination */}
                    {count > 10 && (
                      <Box display="flex" justifyContent="center" mt={4}>
                        <AppPagination
                          handlePageChange={handlePageChange}
                          count={count}
                          page={page}
                        />
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Container>
        </Box>
      </Box>

      {/* Appointment Modal */}
      {open && (
        <AppointmentModal
          open={open}
          onClose={handleClose}
          candidate={candidat}
          onSuccess={handleSave}
        />
      )}
      {openDetailCandidat && (
        <DetailCandidatEvent
          open={openDetailCandidat}
          onClose={handleCloseDetailCandidat}
          candidat={candidat}
          companyId={userData.data.idProfile}
          entrepriseId={userData.data.id}
        />
      )}


      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      />
    </main>
  )
}

export default FavoriteCandidats
